// Office "Auditor access" page (Session AEJ, Director decision D29; office/admin). Generates a
// time-limited MAGIC LINK an external assessor (CHAS / ISO / HSE) clicks to sign in to the
// read-only compliance portal, replacing the persistent auditor password. Generating shows the
// link ONCE (it is never retrievable again); the office copies it and sends it to the auditor.
// The active-links list lets the office see and revoke outstanding links; revoking invalidates a
// link at once (and ends any in-flight session). No password is ever created or shown.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "../../lib/api";
import type { AuditorTokenCreateResult, AuditorTokenListItem } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { useSingleTimeout } from "../../lib/useSingleTimeout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { formatDate } from "../../lib/format";
import { filterRows, useListSearch, type SearchAccessors } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";

const EXPIRY_OPTIONS = [
  { days: 7 as const, label: "7 days" },
  { days: 14 as const, label: "14 days" },
  { days: 30 as const, label: "30 days" },
];

const TOKEN_SEARCH: SearchAccessors<AuditorTokenListItem> = [(t) => t.label, (t) => t.email];
const TOKEN_SORT: SortAccessors<AuditorTokenListItem> = {
  label: (t) => (t.label ?? "").toLowerCase(),
  email: (t) => t.email.toLowerCase(),
  created: (t) => t.createdAt,
  expires: (t) => t.expiresAt,
  lastUsed: (t) => t.lastUsedAt ?? "",
};
const SORT_OPTIONS = [
  { key: "label", label: "Label" },
  { key: "email", label: "Email" },
  { key: "created", label: "Created" },
  { key: "expires", label: "Expires" },
  { key: "lastUsed", label: "Last used" },
];

// Expiry emphasis: red once past, amber within 24 hours, otherwise the default body colour.
function expiryClass(iso: string): string {
  const t = new Date(iso).getTime();
  const now = Date.now();
  if (Number.isNaN(t)) return "text-ele-body";
  if (t <= now) return "text-ele-danger";
  if (t - now <= 24 * 60 * 60 * 1000) return "text-amber-400";
  return "text-ele-body";
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function AuditorAccessPage() {
  const [tokens, setTokens] = useState<AuditorTokenListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<AuditorTokenCreateResult | null>(null);
  const [copied, setCopied] = useState(false);
  const resetCopiedLater = useSingleTimeout();

  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism, the
  // InvoicesPage precedent). jsdom has no matchMedia, so every existing test still renders the
  // table it pins.
  const [mobileCards] = useState(initialMobileLayout);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("auditorAccess");

  const load = useCallback(async () => {
    setLoading(true);
    setListError(false);
    try {
      const res = await api.auditorTokens.list();
      setTokens(res.items);
    } catch {
      setListError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openForm() {
    setEmail("");
    setLabel("");
    setDays(7);
    setFormError(null);
    setGenerated(null);
    setShowForm(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const res = await api.auditorTokens.create({ email: email.trim(), label: label.trim() || undefined, expiryDays: days });
      setGenerated(res);
      setCopied(false);
      setShowForm(false);
      await load();
    } catch (err) {
      // Plain English only: never surface a raw machine code to the office.
      if (err instanceof ApiError && (err.code === "invalid_request" || err.status === 400)) {
        setFormError("Please enter a valid email address.");
      } else {
        setFormError("Could not generate the link. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function doCopy(url: string) {
    const ok = await copyText(url);
    setCopied(ok);
    if (ok) resetCopiedLater(() => setCopied(false), 2000);
  }

  async function doRevoke(t: AuditorTokenListItem) {
    setRevokingId(t.id);
    setRevokeError(null);
    try {
      await api.auditorTokens.revoke(t.id);
      setTokens((xs) => xs.filter((x) => x.id !== t.id));
    } catch {
      setRevokeError("Could not revoke that link. Please try again.");
    } finally {
      setRevokingId(null);
    }
  }

  const canCreate = Boolean(email.trim()) && !saving;
  const visible = useMemo(
    () => sortRows(filterRows(tokens, search, TOKEN_SEARCH), TOKEN_SORT, sort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokens, search, sort.sortKey, sort.sortDir],
  );

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Auditor access"
        subtitle="Generate a temporary, read-only compliance portal link for an external assessor (CHAS, ISO or HSE). Send them the link, they click it to sign in and see only the compliance reports. The link expires automatically, and you can revoke it at any time. No password to remember."
        actions={!showForm ? <Button onClick={openForm}>Generate access link</Button> : undefined}
      />

      {generated && (
        <div role="status" className="mb-6 rounded-ele border border-ele-green bg-ele-surface p-5">
          <h2 className="font-heading text-lg text-ele-green">Access link ready</h2>
          <p className="mt-1 text-sm text-ele-body">
            This link will not be shown again. Copy it now and send it to the auditor at{" "}
            <span className="text-ele-strong">{generated.email}</span>. It expires on{" "}
            <span className="text-ele-strong">{formatDate(generated.expiresAt)}</span>.
          </p>
          <div className="mt-4">
            <label className="mb-1 block text-xs uppercase tracking-widest text-ele-blue" htmlFor="generated-link">
              Access link
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="generated-link"
                type="text"
                readOnly
                value={generated.url}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-mono text-xs outline-none"
              />
              <Button className="sm:shrink-0" onClick={() => void doCopy(generated.url)}>
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>
          </div>
          <div className="mt-4">
            <label className="mb-1 block text-xs uppercase tracking-widest text-ele-blue" htmlFor="generated-email">
              Auditor email (for manual forwarding)
            </label>
            <input
              id="generated-email"
              type="text"
              readOnly
              value={generated.email}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none"
            />
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 max-w-lg rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
          <h2 className="mb-4 font-heading text-xl text-ele-blue">Generate access link</h2>
          <div className="grid gap-4">
            <div>
              <label className="mb-1 block text-sm" htmlFor="auditor-token-email">
                Auditor email<span className="text-ele-green"> *</span>
              </label>
              <input
                id="auditor-token-email"
                type="email"
                autoComplete="off"
                autoCapitalize="none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm" htmlFor="auditor-token-label">
                Label (optional)
              </label>
              <input
                id="auditor-token-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. CHAS audit July 2026"
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm" htmlFor="auditor-token-days">
                Link expires after
              </label>
              <select
                id="auditor-token-days"
                value={days}
                onChange={(e) => setDays(Number(e.target.value) as 7 | 14 | 30)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              >
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.days} value={o.days}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {formError && (
            <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {formError}
            </p>
          )}
          <div className="mt-5 flex gap-3">
            <Button type="submit" disabled={!canCreate}>
              {saving ? "Generating..." : "Generate link"}
            </Button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-ele border border-ele-purple-accent px-4 py-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      {revokeError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {revokeError}
        </p>
      )}

      {!loading && !listError && tokens.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search label or email..."
          searchLabel="Search auditor access links"
          resultCount={{ shown: visible.length, total: tokens.length, noun: "links" }}
          sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {loading ? (
        <Loader label="Loading the auditor access links..." />
      ) : listError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the auditor access links. Please try again in a moment.
        </p>
      ) : tokens.length === 0 ? (
        <EmptyState message="No active auditor access links. Generate one above." />
      ) : visible.length === 0 ? (
        <EmptyState role="status" message={`No auditor access links match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Session ALY mobile card conversion: at 375px the table's surviving Label + Email +
        // Expires + Revoke columns still divide one phone width between them. The card stacks
        // instead, and Created + Last used -- desktop-only until now -- come back as meta/right.
        <MobileCardList label="Auditor access links">
          {visible.map((t) => (
            <MobileListCard
              key={t.id}
              primary={t.label || "-"}
              secondary={t.email}
              meta={`Created ${formatDate(t.createdAt)}`}
              right={
                <>
                  <span className={`text-xs ${expiryClass(t.expiresAt)}`}>Expires {formatDate(t.expiresAt)}</span>
                  <span className="text-xs text-ele-body opacity-70">
                    {t.lastUsedAt ? `Last used ${formatDate(t.lastUsedAt)}` : "Not yet used"}
                  </span>
                </>
              }
              actions={
                <ConfirmButton
                  onConfirm={() => void doRevoke(t)}
                  label="Revoke"
                  confirmLabel="Confirm?"
                  busy={revokingId === t.id}
                  busyLabel="Revoking..."
                  title={`Revoke the access link for ${t.email}${t.label ? ` (${t.label})` : ""}`}
                  className="min-h-11 flex-1 rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs transition hover:border-ele-danger hover:text-ele-danger"
                />
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            {/* Below md the budget is Label (identifier) + Email + Expires + Revoke; Created and
                Last used drop (Expires already carries the urgency colour). */}
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "24%" }} />
              <col className="max-md:hidden" style={{ width: "13%" }} />
              <col style={{ width: "15%" }} />
              <col className="max-md:hidden" style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Label" colKey="label" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Email" colKey="email" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Created" colKey="created" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Expires" colKey="expires" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Last used" colKey="lastUsed" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="cell-actions px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr key={t.id} className="border-t border-ele-purple-accent/40">
                  <td className="px-3 py-2 text-ele-strong">{t.label || "-"}</td>
                  <td className="px-3 py-2">{t.email}</td>
                  <td className="max-md:hidden px-3 py-2">{formatDate(t.createdAt)}</td>
                  <td className={`px-3 py-2 ${expiryClass(t.expiresAt)}`}>{formatDate(t.expiresAt)}</td>
                  <td className="max-md:hidden px-3 py-2">{t.lastUsedAt ? formatDate(t.lastUsedAt) : "Not yet used"}</td>
                  <td className="cell-actions px-3 py-2 text-right">
                    <ConfirmButton
                      onConfirm={() => void doRevoke(t)}
                      label="Revoke"
                      confirmLabel="Confirm?"
                      busy={revokingId === t.id}
                      busyLabel="Revoking..."
                      title={`Revoke the access link for ${t.email}${t.label ? ` (${t.label})` : ""}`}
                      className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs transition hover:border-ele-danger hover:text-ele-danger"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
